import { makeStyles } from '@material-ui/core'
import { Handle, Position } from '@xyflow/react'
import clsx from 'clsx'
import React, { useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { usePublicImageUrl } from '../../../../../../hooks/usePublicResource'
import { useEntityGraphContext } from '../../../Wrapper/EntityGraphProvider'
import { nodePropTypes } from '../../../Canvas/nodes/constants'
import { useGetNodeValues } from '../../../Canvas/nodes/hooks/useGetNodeValues'
import { ICON_NAMES } from '../../../../../../constants'
import Icon from '../../../../../atoms/Icon'
import SydModal from '../../../../../commonDesign/SydModal'
import EditForm from './editForms'

// Helper function to generate hexagon SVG path based on orientation
const getHexagonPath = (orientation) => {
  return orientation === 'flat'
    ? '24,6 76,6 94,50 76,94 24,94 6,50'
    : '50,6 94,28 94,72 50,94 6,72 6,28'
}

const useStyles = makeStyles(() => ({
  container: ({ size, isSlot, orientation }) => {
    const hexPath = getHexagonPath(orientation)
    const hexSvg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><polygon points='${hexPath}' fill='white' stroke='white' stroke-width='12' stroke-linejoin='round' stroke-linecap='round'/></svg>`
    const maskUrl = `url("data:image/svg+xml;utf8,${hexSvg}") center/contain no-repeat`

    return {
      width: size === 'lg' ? 300 : size === 'sm' ? 120 : 160,
      height: size === 'lg' ? 300 : size === 'sm' ? 120 : 160,
      fontSize: size === 'sm' ? '0.75em' : size === 'lg' ? '1.25em' : '1em',
      position: 'relative',
      color: 'var(--xy-node-color, var(--xy-node-color-default))',
      zIndex: !isSlot ? 1000 : 'initial',
      '& .__edit-toggle': {
        opacity: 0,
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'var(--color-primary-400)',
        color: 'var(--color-contrast-primary-400)',
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: 'contain',
        mask: maskUrl,
        WebkitMask: maskUrl,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: 'contain',
        zIndex: 10,
        width: '2.5em',
        height: '2.5em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 0.3s',
        cursor: 'pointer',
        '&:hover': {
          background: 'var(--color-primary-500)',
          color: 'var(--color-contrast-primary-500)'
        }
      },
      '&:hover .__edit-toggle': {
        opacity: 1
      },
      '& .__innerContainer': {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-neutral-200)',
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: 'contain',
        mask: maskUrl,
        WebkitMask: maskUrl,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: 'contain'
      },
      '&:hover .__innerContainer': {
        background: 'linear-gradient(135deg, var(--color-neutral-100), var(--color-neutral-300))'
      },
      '& .__completedBorder': {
        position: 'absolute',
        top: '-10px',
        right: '-10px',
        bottom: '-10px',
        left: '-10px',
        pointerEvents: 'none',
        zIndex: -1,
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: 'contain',
        mask: maskUrl,
        WebkitMask: maskUrl,
        background: 'var(--color-primary-400)'
      }
    }
  },
  content: ({ orientation }) => ({
    position: 'relative',
    zIndex: 2,
    fontWeight: 600,
    padding: '1.6em 1.6em 1em',
    width: '100%',
    height: orientation === 'pointy' ? '70%' : '80%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  }),
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    width: '100%',
    height: '100%'
  },
  progress: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translate(-50%, -75%)',
    zIndex: 2,
    background: 'var(--color-primary-400)',
    color: 'var(--color-contrast-primary-400)',
    borderRadius: 'var(--border-radius-round)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '4em',
    height: '4em'
  },
  header: ({ orientation }) => ({
    fontSize: '1.25em',
    fontWeight: 'var(--font-weight-bold)',
    ...(orientation === 'flat' && ({
      maxWidth: '85%',
      margin: '0 auto'
    }))
  }),
  descriptionWrapper: ({ orientation }) => ({
    flex: 1,
    overflow: 'auto',
    ...(orientation === 'flat' && ({
      maxWidth: '90%',
      margin: '0 auto'
    }))
  }),
  description: {
    '&.__incomplete': {
      opacity: 0.6
    }
  },
  markdownDisplay: {
    width: '100%',
    '& ul': {
      paddingLeft: '0.5em'
    },
    '& li+li': {
      marginTop: '0.5em'
    }
  }
}))

// Generate handle positions based on orientation
const getHandlePositions = (orientation) => {
  if (orientation === 'flat') {
    // Flat-top hexagon: handles at vertices and midpoints
    // Vertices based on actual SVG coordinates: 24,6 76,6 94,50 76,94 24,94 6,50
    return [
      { id: 'top', style: { opacity: 0, top: 0, left: '50%' }, position: Position.Top },
      { id: 'right', style: { opacity: 0, top: '50%', right: 0 }, position: Position.Right },
      { id: 'bottom', style: { opacity: 0, top: '100%', left: '50%' }, position: Position.Bottom },
      { id: 'left', style: { opacity: 0, top: '50%', left: 0 }, position: Position.Left },
      { id: 'rightTop', style: { opacity: 0, top: '25%', left: '90%' }, position: Position.Top },
      { id: 'rightBottom', style: { opacity: 0, top: '75%', left: '90%' }, position: Position.Bottom },
      { id: 'leftBottom', style: { opacity: 0, top: '75%', left: '10%' }, position: Position.Bottom },
      { id: 'leftTop', style: { opacity: 0, top: '25%', left: '10%' }, position: Position.Top }
    ]
  }

  // Pointy-top hexagon (original): handles at vertices (60°, 120°, 240°, 300°) and midpoints
  // Vertices at 0, 60, 120, 180, 240, 300 degrees
  return [
    { id: 'top', style: { opacity: 0, top: 0, left: '50%' }, position: Position.Top },
    { id: 'right', style: { opacity: 0, top: '50%', right: 0 }, position: Position.Right },
    { id: 'bottom', style: { opacity: 0, top: '100%', left: '50%' }, position: Position.Bottom },
    { id: 'left', style: { opacity: 0, top: '50%', left: 0 }, position: Position.Left },
    { id: 'rightTop', style: { opacity: 0, top: '25%', right: 0 }, position: Position.Right },
    { id: 'rightBottom', style: { opacity: 0, top: '75%', right: 0 }, position: Position.Right },
    { id: 'leftBottom', style: { opacity: 0, top: '75%', left: 0 }, position: Position.Left },
    { id: 'leftTop', style: { opacity: 0, top: '25%', left: 0 }, position: Position.Left }
  ]
}

// Are these the bestagons?
// https://www.youtube.com/watch?v=thOifuHs6eY
export default function HexagonNode ({ data, selected, id }) {
  const size = data?.nodeProps?.size || 'md'
  const orientation = data?.nodeProps?.orientation || 'pointy'
  const isSlot = useMemo(() => data.isSlot, [data.isSlot])
  const handlePositions = useMemo(() => getHandlePositions(orientation), [orientation])
  const classes = useStyles({ size, orientation, isSlot })
  const { state, dispatch } = useEntityGraphContext()

  const {
    header,
    description,
    image: _image,
    backgroundColor,
    textColor,
    borderColor
  } = useGetNodeValues(data, state.linkedEntityData)

  const imagePublicUrl = usePublicImageUrl(_image)
  const image = useMemo(() => _image?.length && !_image.startsWith('http') ? imagePublicUrl : _image, [_image, imagePublicUrl])

  const style = useMemo(() => ({
    backgroundColor,
    backgroundImage: image ? `url(${image})` : undefined,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    color: textColor,
    borderColor
  }), [backgroundColor, borderColor, image, textColor])

  const key = useMemo(() => state.linkedEntityData?.[data?.entityProps?.levelTypeId]?.[data?.entityProps?.levelId] ?? data?.entityProps?.levelId ?? 'hexagon', [data?.entityProps, state.linkedEntityData])
  const isIncomplete = useMemo(() => isSlot && !data?.entityProps?.levelId, [data?.entityProps?.levelId, isSlot])
  const itemData = useMemo(() => data?.entityProps?.data, [data])

  // Config Items
  const lifeMapConfig = useMemo(() => state.config.lifeMap || {}, [state.config.lifeMap])
  const canEditContent = useMemo(() => !!data.entityProps?.entityLevelTypeId && lifeMapConfig.canEditSlotContent, [data.entityProps?.entityLevelTypeId, lifeMapConfig])
  const showProgressMeter = useMemo(() => lifeMapConfig.showProgressMeter && !isSlot, [isSlot, lifeMapConfig])
  const hideProgressWhenComplete = useMemo(() => lifeMapConfig.hideProgressWhenComplete, [lifeMapConfig])
  const headerStyles = useMemo(() => lifeMapConfig.headerStyles ?? {}, [lifeMapConfig])
  const contentStyles = useMemo(() => lifeMapConfig.contentStyles ?? {}, [lifeMapConfig])

  const isCompleted = useMemo(() => isSlot && data?.entityProps?.thingId, [isSlot, data?.entityProps?.thingId])

  const progress = useMemo(() => {
    const totalNodes = state.data?.nodes?.filter(node => !!node?.data?.isSlot)?.length ?? 0
    const attachedNodes = state.data?.nodes?.filter(node => !!node?.data?.isSlot && !!node?.data?.entityProps?.levelId)?.length ?? 0

    if (totalNodes === 0) return 0
    return Math.round((attachedNodes / totalNodes) * 100)
  }, [state.data?.nodes])

  const [isEditing, setIsEditing] = useState(false)
  const onSubmitChange = (updatedData) => {
    setIsEditing(false)
    dispatch({
      type: 'UPDATE_NODE',
      payload: {
        nodeId: id,
        path: ['data', 'entityProps'],
        value: {
          ...data.entityProps,
          ...updatedData
        }
      }
    })
  }

  return (
    <>
      {canEditContent && isSlot && (
        <SydModal
          open={isEditing}
          onClose={() => setIsEditing(false)}
          title={`Edit "${header}"`}
          maxWidth='md'
          fullWidth
          showCloseButton
          disablePortal={false}
        >
          <EditForm
            defaultValues={{
              ...data.entityProps,
              chartLevel: {
                levelId: state.levelId,
                levelTypeId: state.levelTypeId
              }
            }}
            levelTypeId={data.entityProps.entityLevelTypeId}
            onSubmit={onSubmitChange}
            onCancel={() => setIsEditing(false)}
          />
        </SydModal>
      )}
      <div
        key={key}
        className={clsx(classes.container, { selected, isSlot })}
      >
        {handlePositions.map(({ position, style, id }) => (
          <Handle
            key={id} id={`handle_${id}`} type='source' position={position} style={style ?? {}}
            className={classes.handle}
          />
        ))}
        {handlePositions.map(({ position, style, id }) => (
          <Handle
            key={id} id={`target_${id}`} type='target' position={position} style={style ?? {}}
            className={classes.handle}
          />
        ))}

        {canEditContent && isSlot && (
          <button onClick={() => setIsEditing(!isEditing)} className='__edit-toggle'>
            <Icon name={ICON_NAMES.edit} />
          </button>
        )}

        <div className='__innerContainer nowheel' style={style}>
          <div className={classes.background} style={style} />

          <div className={classes.content}>
            <div className={classes.header} style={headerStyles}>{header}</div>
            <div className={classes.descriptionWrapper} style={contentStyles}>
              {itemData?.contentType === 'markdown' ? (
                <ReactMarkdown
                  className={clsx(classes.markdownDisplay, classes.description, isIncomplete ? '__incomplete' : '')}
                  remarkPlugins={[remarkGfm]}
                >
                  {description}
                </ReactMarkdown>
              ) : (
                <p className={clsx(classes.description, isIncomplete ? '__incomplete' : '')}>{description}</p>
              )}
            </div>
          </div>
        </div>

        {isCompleted && <div className='__completedBorder' />}

        {!isSlot && showProgressMeter && (!hideProgressWhenComplete || progress < 100) && (
          <div className={classes.progress}>
            <span>{progress}%</span>
          </div>
        )}
      </div>
    </>
  )
}

HexagonNode.propTypes = nodePropTypes
